import { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { API } from "@/App";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogClose } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Clock, CheckCircle2, MailOpen, MousePointerClick, AlertOctagon, XCircle,
  RefreshCw, Send, Pencil, Search, ShieldAlert, Info, Download,
} from "lucide-react";
import { toast } from "sonner";

const fmt = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("es-MX", { dateStyle: "short", timeStyle: "short" });
  } catch { return iso; }
};

const StatusBadge = ({ status, bounceReason }) => {
  const config = {
    sent:      { label: "Enviado",   Icon: Clock,             cls: "bg-slate-100 text-slate-700 border-slate-200" },
    delivered: { label: "Entregado", Icon: CheckCircle2,      cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
    opened:    { label: "Abierto",   Icon: MailOpen,          cls: "bg-cyan-50 text-cyan-700 border-cyan-200" },
    clicked:   { label: "Click",     Icon: MousePointerClick, cls: "bg-indigo-50 text-indigo-700 border-indigo-200" },
    bounced:   { label: "Rebotado",  Icon: AlertOctagon,      cls: "bg-rose-50 text-rose-700 border-rose-200" },
    failed:    { label: "Fallo",     Icon: XCircle,           cls: "bg-red-50 text-red-700 border-red-200" },
  };
  const c = config[status] || config.sent;
  const Icon = c.Icon;
  return (
    <div className="inline-flex flex-col items-start gap-0.5">
      <Badge variant="outline" className={`gap-1 border ${c.cls}`}>
        <Icon className="w-3 h-3" />{c.label}
      </Badge>
      {bounceReason && <span className="text-[10px] text-rose-600 max-w-[200px] truncate" title={bounceReason}>{bounceReason}</span>}
    </div>
  );
};

export default function InvitationsDialog({ project, onClose }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [scanning, setScanning] = useState(false);
  const [editing, setEditing] = useState(null); // {invitation, newEmail}
  const [resending, setResending] = useState(null);
  const [q, setQ] = useState("");
  const [exporting, setExporting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/viajeros-ext/projects/${project.id}/invitations`, { withCredentials: true });
      setRows(res.data?.invitations || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudieron cargar las invitaciones");
    } finally {
      setLoading(false);
    }
  }, [project.id]);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    if (!q.trim()) return rows;
    const qn = q.trim().toLowerCase();
    return rows.filter((r) => (r.email || "").toLowerCase().includes(qn) || (r.status || "").toLowerCase().includes(qn));
  }, [rows, q]);

  const stats = useMemo(() => {
    const s = { total: 0, enviado: 0, abierto: 0, click: 0, rebotado: 0 };
    rows.forEach((r) => {
      s.total++;
      if (r.status === "sent" || r.status === "delivered") s.enviado++;
      if (r.status === "opened" || (r.open_count || 0) > 0) s.abierto++;
      if (r.status === "clicked" || (r.click_count || 0) > 0) s.click++;
      if (r.status === "bounced") s.rebotado++;
    });
    return s;
  }, [rows]);

  const scanBounces = async () => {
    setScanning(true);
    try {
      const res = await axios.post(`${API}/viajeros-ext/projects/${project.id}/invitations/scan-bounces`, {}, { withCredentials: true });
      if (res.data?.ok) {
        toast.success(`Revisión completada. ${res.data.found || 0} rebotes detectados.`);
        load();
      } else {
        toast.warning(res.data?.detail || "No se pudo escanear rebotes");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error escaneando rebotes");
    } finally {
      setScanning(false);
    }
  };

  const exportExcel = async () => {
    setExporting(true);
    try {
      const res = await axios.get(`${API}/viajeros-ext/projects/${project.id}/invitations.xlsx`, {
        withCredentials: true,
        responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      const safe = (project.nombre || "proyecto").replace(/[^A-Za-z0-9_-]+/g, "_");
      const a = document.createElement("a");
      a.href = url;
      a.download = `invitaciones_${safe}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Excel descargado");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo generar el Excel");
    } finally {
      setExporting(false);
    }
  };


  const doResend = async (invitation, newEmail) => {
    setResending(invitation.id);
    try {
      const payload = {};
      if (newEmail && newEmail !== invitation.email) payload.email = newEmail.trim().toLowerCase();
      const res = await axios.post(
        `${API}/viajeros-ext/projects/${project.id}/invitations/${invitation.id}/resend`,
        payload,
        { withCredentials: true }
      );
      if (res.data?.ok) {
        toast.success(`Reenviado a ${res.data.invitation?.email || newEmail || invitation.email}`);
        setEditing(null);
        load();
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo reenviar");
    } finally {
      setResending(null);
    }
  };

  return (
    <Dialog open={true} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-5xl p-0" data-testid="invitations-dialog">
        <DialogHeader className="px-6 pt-5 pb-3 border-b">
          <DialogTitle className="flex items-center gap-2 text-slate-900">
            <Send className="w-5 h-5 text-indigo-600" />
            Invitaciones — <span className="text-slate-600 font-normal">{project.nombre}</span>
          </DialogTitle>
          <p className="text-xs text-slate-500 mt-1">Seguimiento de los links enviados por correo con estatus en tiempo real (apertura, clics y rebotes).</p>
        </DialogHeader>

        <div className="px-6 py-3 border-b bg-slate-50/70">
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
            {[
              { l: "Total", v: stats.total, cls: "text-slate-900" },
              { l: "Enviados", v: stats.enviado, cls: "text-slate-700" },
              { l: "Abiertos", v: stats.abierto, cls: "text-cyan-700" },
              { l: "Con clic", v: stats.click, cls: "text-indigo-700" },
              { l: "Rebotados", v: stats.rebotado, cls: "text-rose-700" },
            ].map((k) => (
              <div key={k.l} className="bg-white border rounded-md px-3 py-2">
                <div className="text-[10px] font-semibold text-slate-500 uppercase tracking-wide">{k.l}</div>
                <div className={`text-xl font-bold ${k.cls}`}>{k.v}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="px-6 py-3 flex flex-wrap items-center gap-2 border-b">
          <div className="relative flex-1 min-w-[220px] max-w-md">
            <Search className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input className="pl-8 h-9" placeholder="Buscar por correo o estatus…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="inv-search" />
          </div>
          <div className="flex-1" />
          <Button size="sm" variant="outline" onClick={load} disabled={loading} data-testid="inv-refresh">
            <RefreshCw className={`w-4 h-4 mr-1.5 ${loading ? "animate-spin" : ""}`} /> Refrescar
          </Button>
          <Button size="sm" variant="outline" onClick={exportExcel} disabled={exporting || rows.length === 0} className="border-emerald-300 text-emerald-700 hover:bg-emerald-50" data-testid="inv-export-xlsx">
            <Download className={`w-4 h-4 mr-1.5 ${exporting ? "animate-pulse" : ""}`} /> {exporting ? "Generando…" : "Excel"}
          </Button>
          <Button size="sm" variant="outline" onClick={scanBounces} disabled={scanning} className="border-rose-300 text-rose-700 hover:bg-rose-50" data-testid="inv-scan-bounces">
            <ShieldAlert className={`w-4 h-4 mr-1.5 ${scanning ? "animate-pulse" : ""}`} /> Buscar rebotes
          </Button>
        </div>

        <div className="max-h-[52vh] overflow-y-auto">
          <Table>
            <TableHeader className="sticky top-0 bg-slate-50 z-10">
              <TableRow>
                <TableHead className="w-[250px]">Correo</TableHead>
                <TableHead>Estatus</TableHead>
                <TableHead>Enviado</TableHead>
                <TableHead>Abierto</TableHead>
                <TableHead>Click</TableHead>
                <TableHead>Enviado por</TableHead>
                <TableHead className="text-right">Acciones</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading && [...Array(3)].map((_, i) => (
                <TableRow key={i}>{[...Array(7)].map((__, j) => <TableCell key={j}><Skeleton className="h-4 w-full" /></TableCell>)}</TableRow>
              ))}
              {!loading && filtered.length === 0 && (
                <TableRow><TableCell colSpan={7} className="text-center py-10 text-slate-400">Aún no hay invitaciones enviadas.</TableCell></TableRow>
              )}
              {!loading && filtered.map((r) => (
                <TableRow key={r.id} className="hover:bg-slate-50" data-testid={`inv-row-${r.id}`}>
                  <TableCell className="font-medium text-slate-800 truncate max-w-[240px]" title={r.email}>{r.email}</TableCell>
                  <TableCell><StatusBadge status={r.status} bounceReason={r.bounce_reason} /></TableCell>
                  <TableCell className="text-xs text-slate-600 whitespace-nowrap">{fmt(r.sent_at)}</TableCell>
                  <TableCell className="text-xs">
                    {r.open_count ? (
                      <div>
                        <div className="text-cyan-700 font-semibold">{r.open_count}×</div>
                        <div className="text-[10px] text-slate-500">{fmt(r.first_opened_at || r.opened_at)}</div>
                      </div>
                    ) : "—"}
                  </TableCell>
                  <TableCell className="text-xs">
                    {r.click_count ? (
                      <div>
                        <div className="text-indigo-700 font-semibold">{r.click_count}×</div>
                        <div className="text-[10px] text-slate-500">{fmt(r.first_clicked_at || r.clicked_at)}</div>
                      </div>
                    ) : "—"}
                  </TableCell>
                  <TableCell className="text-xs text-slate-600 truncate max-w-[160px]" title={r.sent_by_name}>{r.sent_by_name || "—"}</TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-1">
                      <Button variant="ghost" size="sm" className="h-7 w-7 p-0" title="Editar correo y reenviar"
                              onClick={() => setEditing({ invitation: r, newEmail: r.email })}
                              data-testid={`inv-edit-${r.id}`}>
                        <Pencil className="w-3.5 h-3.5" />
                      </Button>
                      <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-indigo-600" title="Reenviar mismo correo"
                              disabled={resending === r.id}
                              onClick={() => doResend(r, r.email)}
                              data-testid={`inv-resend-${r.id}`}>
                        <Send className={`w-3.5 h-3.5 ${resending === r.id ? "animate-pulse" : ""}`} />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>

        <DialogFooter className="px-6 py-3 border-t bg-slate-50/50 flex-col sm:flex-row items-start sm:items-center gap-2">
          <p className="text-[11px] text-slate-500 flex items-start gap-1.5 flex-1">
            <Info className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
            La apertura se detecta con un pixel invisible (puede fallar si el cliente bloquea imágenes).
            Los rebotes se detectan al presionar &quot;Buscar rebotes&quot; (revisa la bandeja del remitente).
          </p>
          <DialogClose asChild>
            <Button variant="outline" size="sm">Cerrar</Button>
          </DialogClose>
        </DialogFooter>

        {/* Sub-modal: editar email antes de reenviar */}
        {editing && (
          <Dialog open={true} onOpenChange={(o) => !o && setEditing(null)}>
            <DialogContent className="max-w-md" data-testid="inv-edit-dialog">
              <DialogHeader>
                <DialogTitle>Editar correo y reenviar</DialogTitle>
              </DialogHeader>
              <div className="space-y-3 py-2">
                <div>
                  <label className="text-xs font-semibold text-slate-600 uppercase tracking-wide">Correo original</label>
                  <div className="text-sm text-slate-800">{editing.invitation.email}</div>
                </div>
                <div>
                  <label className="text-xs font-semibold text-slate-600 uppercase tracking-wide">Nuevo correo</label>
                  <Input value={editing.newEmail} onChange={(e) => setEditing((s) => ({ ...s, newEmail: e.target.value }))}
                         type="email" data-testid="inv-edit-new-email" />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setEditing(null)}>Cancelar</Button>
                <Button onClick={() => doResend(editing.invitation, editing.newEmail)}
                        disabled={!editing.newEmail || !editing.newEmail.includes("@") || resending}
                        data-testid="inv-edit-confirm">
                  <Send className="w-4 h-4 mr-1.5" /> Reenviar
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </DialogContent>
    </Dialog>
  );
}
